import { canEditBoard, SHARE_ROLE_LABELS, totalItems, type ProjectMember, type ShareRole, type User } from '@blueprint/domain'
import { useCallback, useEffect, useState } from 'react'
import { Button, Field, TextInput } from '../../components/controls.js'
import { Dialog } from '../../components/Dialog.js'
import { useBoardStore } from '../../state/board-store.js'
import { sessionRepository, useSessionStore } from '../../state/session-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from '../admin/AdminView.module.css'

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

/** The project dialogs, one at a time: members, deletion, or moving an item. */
export function ProjectDialogs() {
  const dialog = useUiStore((state) => state.projectDialog)
  if (!dialog) return null
  if (dialog.kind === 'move') return <MoveBoardDialog boardId={dialog.boardId} />
  if (dialog.kind === 'members') return <ProjectMembersDialog projectId={dialog.projectId} />
  return <ProjectDeleteDialog projectId={dialog.projectId} />
}

/**
 * A project's members, chosen from colleagues in the same tenant. The owner manages them;
 * everyone else in the project sees the list read-only.
 */
function ProjectMembersDialog({ projectId }: { projectId: string }) {
  const close = useUiStore((state) => state.setProjectDialog)
  const me = useSessionStore((state) => state.user)
  const project = useBoardStore((state) => state.projects.find((entry) => entry.id === projectId))
  const repository = sessionRepository()
  const manage = project?.role === 'owner'

  const [members, setMembers] = useState<ProjectMember[]>([])
  const [people, setPeople] = useState<User[]>([])
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<ShareRole>('editor')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const [current, tenantUsers] = await Promise.all([repository.listProjectMembers(projectId), manage ? repository.listUsers() : Promise.resolve([])])
      setMembers(current)
      setPeople(tenantUsers)
      setError(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Members could not be loaded.')
    }
  }, [projectId, repository, manage])

  useEffect(() => {
    void load()
  }, [load])

  const done = () => {
    close(null)
    // The sidebar shows member counts.
    void useBoardStore.getState().loadProjects()
  }

  const saved = (next: Promise<ProjectMember[]>, failure: string) =>
    next.then(setMembers).catch((caught: unknown) => setError(caught instanceof Error ? caught.message : failure))

  const already = new Set(members.map((entry) => entry.userId))
  const candidates = people.filter((person) => person.id !== project?.ownerId && person.id !== me?.id && !already.has(person.id))

  const add = () => {
    if (!userId || busy) return
    setBusy(true)
    void saved(repository.setProjectMember(projectId, userId, role), 'That member could not be added.').finally(() => {
      setUserId('')
      setBusy(false)
    })
  }

  return (
    <Dialog open title={`Members of "${project?.name ?? 'project'}"`} onClose={done} onSubmit={add} footer={<Button onClick={done}>Done</Button>}>
      <div className={styles.form}>
        <p className={styles.hint}>
          Members reach every storymap, mindmap and workflow in this project. Owner: {project?.ownerName || 'unknown'}.
        </p>

        {manage ? (
          <div className={styles.field}>
            <span className={styles.label}>Add someone</span>
            <div className={styles.actions} style={{ justifyContent: 'flex-start' }}>
              <select className={styles.input} aria-label="Colleague" value={userId} onChange={(event) => setUserId(event.target.value)}>
                <option value="">Choose a colleague…</option>
                {candidates.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.displayName ? `${person.displayName} (${person.email})` : person.email}
                  </option>
                ))}
              </select>
              <select className={styles.input} aria-label="Role" value={role} onChange={(event) => setRole(event.target.value as ShareRole)}>
                <option value="editor">{SHARE_ROLE_LABELS.editor}</option>
                <option value="viewer">{SHARE_ROLE_LABELS.viewer}</option>
              </select>
              <Button type="submit" disabled={!userId || busy}>
                Add
              </Button>
            </div>
          </div>
        ) : null}

        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Role</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {members.map((entry) => (
              <tr key={entry.userId} data-testid="member-row" data-member-email={entry.email}>
                <td className={styles.strong}>{entry.displayName || entry.email}</td>
                <td>
                  {manage ? (
                    <select
                      className={styles.input}
                      aria-label={`Role for ${entry.email}`}
                      value={entry.role}
                      onChange={(event) => void saved(repository.setProjectMember(projectId, entry.userId, event.target.value as ShareRole), 'That change could not be saved.')}
                    >
                      <option value="editor">{SHARE_ROLE_LABELS.editor}</option>
                      <option value="viewer">{SHARE_ROLE_LABELS.viewer}</option>
                    </select>
                  ) : (
                    SHARE_ROLE_LABELS[entry.role]
                  )}
                </td>
                <td className={styles.actions}>
                  {manage ? (
                    <Button variant="danger" onClick={() => void saved(repository.removeProjectMember(projectId, entry.userId), 'That member could not be removed.')}>
                      Remove
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
            {members.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={3}>
                  No members yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  )
}

/**
 * Deleting a project deletes everything in it, for every member. The dialog says exactly
 * what will go, and the project's name must be typed before the button is enabled.
 */
function ProjectDeleteDialog({ projectId }: { projectId: string }) {
  const close = useUiStore((state) => state.setProjectDialog)
  const project = useBoardStore((state) => state.projects.find((entry) => entry.id === projectId))
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!project) return null
  const confirmed = typed.trim() === project.name

  const remove = () => {
    if (!confirmed || busy) return
    setBusy(true)
    sessionRepository()
      .deleteProject(project.id)
      .then(async () => {
        close(null)
        await useBoardStore.getState().loadProjects()
      })
      .catch((caught: unknown) => {
        setBusy(false)
        setError(caught instanceof Error ? caught.message : 'The project could not be deleted.')
      })
  }

  const counts = project.itemCounts
  return (
    <Dialog
      open
      title={`Delete project "${project.name}"`}
      onClose={() => close(null)}
      onSubmit={remove}
      footer={
        <>
          <Button onClick={() => close(null)}>Cancel</Button>
          <Button variant="danger" type="submit" disabled={!confirmed || busy}>
            Delete project and everything in it
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <p className={styles.error} role="alert">
          This permanently deletes the project and everything in it, for everyone who has access. It cannot be undone.
        </p>
        <ul data-testid="project-delete-counts">
          <li>{plural(counts.storymap, 'storymap')}</li>
          <li>{plural(counts.mindmap, 'mindmap')}</li>
          <li>{plural(counts.workflow, 'workflow')}</li>
        </ul>
        {project.memberCount > 0 ? <p className={styles.hint}>{plural(project.memberCount, 'member')} will lose access.</p> : null}
        <Field label={`Type "${project.name}" to confirm`} error={error}>
          {(id) => <TextInput id={id} data-autofocus value={typed} autoComplete="off" onChange={(event) => setTyped(event.target.value)} />}
        </Field>
        {totalItems(project) === 0 ? <p className={styles.hint}>The project is empty.</p> : null}
      </div>
    </Dialog>
  )
}

/** Moves one item into another project the user can edit. */
function MoveBoardDialog({ boardId }: { boardId: string }) {
  const close = useUiStore((state) => state.setProjectDialog)
  const board = useBoardStore((state) => state.boards.find((entry) => entry.id === boardId))
  const projects = useBoardStore((state) => state.projects)
  const targets = projects.filter((project) => canEditBoard(project.role) && project.id !== board?.projectId)
  const [projectId, setProjectId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const move = () => {
    if (!projectId) return
    sessionRepository()
      .moveBoard(boardId, projectId)
      .then(async () => {
        close(null)
        await useBoardStore.getState().loadProjects()
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'The item could not be moved.'))
  }

  return (
    <Dialog
      open
      title={`Move "${board?.name ?? 'item'}"`}
      onClose={() => close(null)}
      onSubmit={move}
      footer={
        <>
          <Button onClick={() => close(null)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!projectId}>
            Move
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {targets.length === 0 ? (
          <p className={styles.hint}>There is no other project you can edit.</p>
        ) : (
          <label className={styles.field}>
            <span className={styles.label}>Project</span>
            <select className={styles.input} value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              <option value="">Choose a project…</option>
              {targets.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  )
}
